import {
  ApiComponents,
  ComponentType,
  createComponent,
  PrizeDetailsComponent,
} from "../../../(custom-header)/winners-template/ComponentsMapper";
import { TemplateType } from "../../../(custom-header)/winners-template/templateConfigs";

export const getResultsTitle = (template: TemplateType | undefined, data: PrizeDetailsComponent["data"]): string => {
  switch (template) {
    case "DreamCarWinner":
      return "DREAM CAR COMPETITION WINNER & RESULTS";
    case "HouseWinner":
      return "LUXURY HOME COMPETITION WINNER & RESULTS";
    case "SinglePrizeCompWinner":
      return `${data.competitionName} COMPETITION WINNERS & RESULTS`;
    case "MultiplePrizeCompWinner":
      return `${data.competitionName} COMPETITION WINNERS & RESULTS`;
    case "InstantWinWinner": {
      return `${data.competitionName} COMPETITION WINNERS & RESULTS`;
    }
    default:
      return "COMPETITION WINNER & RESULTS";
  }
};

export const getPrizeWonButtonText = (template: TemplateType | undefined): string => {
  switch (template) {
    case "HouseWinner":
      return "WIN YOUR DREAM HOUSE";
    case "DreamCarWinner":
    case "SinglePrizeCompWinner":
      return "PLAY FOR THIS PRIZE";
    case "MultiplePrizeCompWinner":
      return "SEE TODAY’S FREE COMPETITION";
    case "InstantWinWinner":
      return "ENTER FOR A CHANCE TO WIN";
    default:
      return "PLAY FOR THIS PRIZE";
  }
};

export const getJudgingSideInformationDescription = (template: TemplateType): string => {
  switch (template) {
    case "DreamCarWinner": {
      return (
        "The panel of judges is formed of experienced sports professionals on a rotating basis, who each offer their\n" +
        "            best opinion. Using a panel of judges ensures that nobody can ever know the original position of the ball -\n" +
        "            resulting in a process that is fair, transparent, audited and honest."
      );
    }
    default: {
      return (
        "The draw was undertaken by PromoVeritas, the UK's leading independent promotional verification service.\n\n" +
        "Registration number: 4437132"
      );
    }
  }
};

export const getJudgingSideInformationTitle = (template: TemplateType): string => {
  switch (template) {
    case "DreamCarWinner": {
      return "HOW DO YOU KNOW IT'S SAFE?";
    }
    default: {
      return "";
    }
  }
};

export const divideEtImperaComponents = (
  component: ApiComponents,
  template?: TemplateType,
  isMobile?: boolean,
  allComponents?: ApiComponents[]
): Array<ApiComponents> => {
  switch (component.type) {
    case ComponentType.Header: {
      const { data } = component;
      return [
        createComponent(ComponentType.SectionTitle, "section-title-1", {
          title: data.title,
        }),

        createComponent(ComponentType.Quote, "quote-1", {
          quote: data.subtitle,
          author: data.title,
        }),

        createComponent(ComponentType.Video, "video", {
          assets: data.assets,
          textDesc: "Won for just",
          prizeText: data.ticketPriceWon,
          className: "max-lg:-mx-2.5",
        }),

        createComponent(ComponentType.CompetitionCard, "competition-card", {
          widgetCompetitionId: data.widgetCompetitionId,
          ticketPriceWon: data.ticketPriceWon,
          title: data.title,
          description: `Win this amazing prize for just ${data.ticketPriceWon}!`,
        }),
      ];
    }
    case ComponentType.PrizeDetails: {
      const { data } = component;
      const headerComponent = allComponents?.find(c => c.type === ComponentType.Header);
      const widgetCompetitionId = (headerComponent?.data as { widgetCompetitionId?: number })?.widgetCompetitionId;
      return [
        createComponent(ComponentType.ResultsDate, "results-date", {
          title: getResultsTitle(template, data),
          date: data.resultDate,
          competitionId: data.competitionId,
        }),

        createComponent(ComponentType.PrizeWon, "prize-won", {
          title: "Prize won",
          subtitle: "Subtitle",
          prizeName: data.prizeName,
          prizeUrl: data.prizeUrl,
          buttonText: getPrizeWonButtonText(template),
        }),

        createComponent(ComponentType.TheStory, "the-story", data),

        ...(isMobile
          ? [
              createComponent(ComponentType.AsideTitle, "aside-title-4", {
                title: "Picks of the week",
              }),
              createComponent(ComponentType.ExploreCompetitions, "explore-competitions-picks", {
                competitionIds: [widgetCompetitionId, ...(data.picksOfTheWeekCompetitionIds ?? [])].filter(
                  (item): item is number => typeof item === "number"
                ),
                breakpoints: {
                  200: { slidesPerView: 1.5, spaceBetween: 12, slidesPerGroup: 1 },
                },
              }),
            ]
          : [
              createComponent(ComponentType.AsideTitle, "aside-title-1", {
                title: "Picks of the week",
              }),
              ...(data.picksOfTheWeekCompetitionIds ?? []).map((id, idx) =>
                createComponent(ComponentType.AsideCompetition, `aside-competition-${idx + 1}`, {
                  competitionId: id,
                  subtitle: "Starting from:",
                })
              ),
            ]),

        createComponent(ComponentType.ExtraPrizesWrapper, "extra-prizes-wrapper"),

        createComponent(ComponentType.FeelingLuckyTitle, "feeling-lucky-title", {
          title: "🍀 Feeling lucky?",
        }),
        createComponent(ComponentType.FeaturedFeelingLucky, "featured-feeling-lucky", {
          feelingLuckyPrizeId: data.feelingLuckyPrizeId,
        }),
        createComponent(ComponentType.FeelingLuckyDailySpin, "feeling-lucky-daily-spin", {
          title: "BOTB Pass Competitions",
          subtitle:
            "<span style='color: #F2FF00'>1,250 daily extra prizes</span> <br class='max-lg:hidden' />with BOTB pass!",
          buttonText: "FIND OUT MORE",
        }),

        ...(data.multipleWinners?.length
          ? [
              createComponent(ComponentType.MultipleWinners, "multiple-winners", {
                multipleWinners: data.multipleWinners,
              }),
            ]
          : []),

        ...(data.instantWinWinnersUrl && data.competitionId && !data.multipleWinners?.length
          ? [
              createComponent(ComponentType.TopWinners, "top-winners", {
                label: "OUR TOP WINNERS",
                competitionId: data.competitionId,
                instantWinWinnersUrl: data.instantWinWinnersUrl,
              }),
              createComponent(ComponentType.AllWinnersPrizes, "all-winners-prizes", {
                competitionId: data.competitionId,
                instantWinWinnersUrl: data.instantWinWinnersUrl,
              }),
            ]
          : []),
      ];
    }
    case ComponentType.ExtraPrizes: {
      const { data } = component;

      const playersCount = data.reduce((sum, item) => sum + item.data.numberOfPlayers, 0);
      const prizeValueCount = data.reduce((sum, item) => sum + item.data.prizeValue, 0);

      return [
        createComponent(ComponentType.ExtraPrizesHeader, "extra-prizes-header", {
          players: playersCount,
          prize: prizeValueCount,
        }),

        ...data.map(extraPrize => {
          switch (extraPrize.type) {
            case ComponentType.ExtraPrizesRunnersUp:
              return createComponent(ComponentType.ExtraPrizesRunnersUp, `extra-prizes-${extraPrize.type}`, extraPrize);
            case ComponentType.ExtraPrizesClosestByCar:
              return createComponent(
                ComponentType.ExtraPrizesClosestByCar,
                `extra-prizes-${extraPrize.type}`,
                extraPrize
              );
          }
        }),
      ];
    }
    case ComponentType.GamePhoto: {
      const { data } = component;

      return [
        createComponent(ComponentType.WinnersAreTitle, `winners-are-title`, {
          title: "And the Winners Are...",
        }),
        createComponent(ComponentType.WinnersGamePhoto, `winners-are-game-photo`, {
          data,
        }),
        createComponent(ComponentType.WinnersNearMisses, `winners-are-near-misses`, {
          data,
        }),
        createComponent(ComponentType.WinnersRewardScheme, `winners-are-reward-scheme`, {
          data,
        }),
      ];
    }
    case ComponentType.Judging: {
      const { data } = component;
      return [
        createComponent(ComponentType.Chance, "chance", {
          data: {
            widgetCompetitionId: data.widgetCompetitionId,
          },
        }),
        createComponent(ComponentType.JudgingTitle, "judging-title", {
          title: data.shouldShowJudgeSection ? "Judged by Experts, Trusted for 25 Years" : "",
        }),
        createComponent(ComponentType.WinnersAreTitle, `winner-is`, {
          title: data.shouldShowJudgeSection ? "And the Winner is..." : "",
        }),
        createComponent(ComponentType.JudgingMain, "judging-main", {
          data,
        }),
        createComponent(ComponentType.JudgingSideInformation, "judging-side-information", {
          data: {
            ...data,
            title: getJudgingSideInformationTitle(template),
            description: getJudgingSideInformationDescription(template),
            disableJudgingVideo: !data.judgesMediaUrl || template !== "DreamCarWinner",
            disableConfirmationLetter: !data.confirmationLetterUrl,
            disableMeetTheJudges: template !== "DreamCarWinner",
            disableJudgingFAQs: template !== "DreamCarWinner",
            template,
            shouldShowJudgeSection: data.shouldShowJudgeSection,
          },
        }),
        createComponent(ComponentType.SkillToLuck, "skill-to-luck", {
          items: data.preferSkillToLuckPrizes,
          title: "Prefer skill to luck?",
          subtitle: "Dream Car Competition",
          assets: [
            {
              src: "/assets/images/winners/spot-the-ball.webp",
              alt: "competition-thumbnail",
              width: 67,
              height: 74,
              className: "w-[67px] lg:w-[87px] h-auto",
            },
            {
              src: "/assets/images/winners/cars-only.webp",
              alt: "competition-thumbnail",
              width: 67,
              height: 74,
              className: "w-[67px] lg:w-[87px] h-auto",
            },
          ],
        }),
        createComponent(ComponentType.ExploreCompetitions, "explore-competitions", {
          competitionType: "featured",
          title: "Explore our competitions",
        }),
      ];
    }
    case ComponentType.PrizeMedia: {
      const { data } = component;

      return [
        createComponent(ComponentType.HouseSlider, "house-slider", {
          data,
        }),
      ];
    }
    case ComponentType.TopWinners: {
      return [component];
    }
    default: {
      return [component];
    }
  }
};

export const extractFilenameFromContentDisposition = (headerValue: string): string | null => {
  if (!headerValue) {
    return null;
  }

  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i;
  const matches = headerValue.match(filenameRegex);

  if (matches && matches[1]) {
    let filename = matches[1];

    filename = filename.replace(/['"]/g, "");

    // Handle RFC 5987 encoded filenames (e.g., UTF-8''encoded%20name.jpg)
    if (filename.includes("''")) {
      filename = filename.split("''")[1];
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {
        // If decoding fails, use the original filename
      }
    }

    return filename.trim();
  }

  return null;
};
